import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LockKeyhole, User, LogIn, ShieldCheck } from 'lucide-react';

const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:5001/api'
  : 'https://dpass.architectcrm.in/api';

export default function LoginPage() {
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();

    setError('');

    if (!username.trim() || !password.trim()) {
      setError('Please enter username and password.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username.trim(),
          password: password.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Invalid username or password.');
      }

      // Store JWT
      localStorage.setItem('authToken', data.token);

      // Redirect to domains dashboard
      navigate('/', { replace: true });

    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background:
          'linear-gradient(135deg, #eef2ff 0%, #f8fafc 50%, #ecfeff 100%)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '430px',
          background: '#ffffff',
          borderRadius: '24px',
          padding: '38px',
          boxShadow: '0 25px 60px rgba(15, 23, 42, 0.12)',
          border: '1px solid #e2e8f0',
        }}
      >
        {/* Logo / Icon */}
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '18px',
            background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            boxShadow: '0 10px 25px rgba(99, 102, 241, 0.25)',
          }}
        >
          <ShieldCheck size={34} color="#fff" />
        </div>

        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1
            style={{
              margin: 0,
              color: '#0f172a',
              fontSize: '28px',
              fontWeight: '800',
            }}
          >
            Domain Manager
          </h1>

          <p
            style={{
              marginTop: '8px',
              color: '#64748b',
              fontSize: '14px',
            }}
          >
            Sign in to manage your domains
          </p>
        </div>

        {error && (
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              borderRadius: '12px',
              padding: '12px 14px',
              marginBottom: '20px',
              fontSize: '14px',
              fontWeight: '600',
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleLogin}>
          {/* Username */}
          <div style={{ marginBottom: '18px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '700',
                color: '#334155',
              }}
            >
              Username
            </label>

            <div style={{ position: 'relative' }}>
              <User
                size={19}
                color="#6366f1"
                style={{
                  position: 'absolute',
                  left: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              />

              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                autoComplete="username"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  height: '50px',
                  padding: '0 15px 0 45px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '12px',
                  outline: 'none',
                  fontSize: '14px',
                  background: '#f8fafc',
                }}
              />
            </div>
          </div>

          {/* Password */}
          <div style={{ marginBottom: '25px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '700',
                color: '#334155',
              }}
            >
              Password
            </label>

            <div style={{ position: 'relative' }}>
              <LockKeyhole
                size={19}
                color="#6366f1"
                style={{
                  position: 'absolute',
                  left: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              />

              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                autoComplete="current-password"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  height: '50px',
                  padding: '0 15px 0 45px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '12px',
                  outline: 'none',
                  fontSize: '14px',
                  background: '#f8fafc',
                }}
              />
            </div>
          </div>

          {/* Login Button */}
          <button
            type="submit"
            style={{
              width: '100%',
              height: '52px',
              border: 'none',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
              color: '#ffffff',
              fontSize: '15px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '9px',
              boxShadow: '0 10px 20px rgba(99, 102, 241, 0.25)',
            }}
          >
            <LogIn size={19} />
            Sign In
          </button>
        </form>
      </div>
    </div>
  );
}